import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { setProject } from "../reducers/AuthSlice";
import "./NavPageStyle.css";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import { DesktopIndexMenu } from "./DesktopIndexMenu";
import IndexInfo from "./IndexInfo";
import useIndices from "../hooks/useIndices";
import AddMenu from "./AddMenu";
import AccountMenu from "./AccountMenu";
import logo from "../assets/bluebell-lockup.png";

export default function NavPageSelector({
  collapsed,
  onToggleCollapse,
  children,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const indices = useIndices();
  const projectName = useSelector((state) => state.auth?.projectName);
  const projects = useSelector((state) => state.auth?.projects || {});
  const currentIndex = useSelector((state) => state.categories?.currentIndex);

  const projectOptions = Object.entries(projects).map(([name, id]) => ({
    name,
    id,
  }));

  const handleProjectSelect = (_, newValue) => {
    if (newValue) {
      dispatch(
        setProject({ projectName: newValue.name, projectId: newValue.id }),
      );
      window.location.reload();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {/* Logo and account */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box component="img" src={logo} alt="Bluebell" sx={{ height: 28 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Tooltip title={t("map.dashboard")}>
            <IconButton
              onClick={() => navigate("/dashboard")}
              size="small"
              aria-label={t("map.dashboard")}
              sx={{ color: "primary.main", p: "6px" }}
            >
              <DashboardOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
          <AccountMenu />
          <Tooltip
            title={collapsed ? t("map.expandPanel") : t("map.collapsePanel")}
          >
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              aria-label={
                collapsed ? t("map.expandPanel") : t("map.collapsePanel")
              }
              aria-expanded={!collapsed}
              sx={{ p: "6px", mr: "-6px" }}
            >
              {collapsed ? (
                <ExpandMoreIcon sx={{ fontSize: 22 }} />
              ) : (
                <ExpandLessIcon sx={{ fontSize: 22 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={!collapsed}>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1.5 }}
        >
          {/* Project selector autocomplete */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(option) => option.name}
              value={projectOptions.find((p) => p.name === projectName) || null}
              onChange={handleProjectSelect}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name
              }
              disableClearable
              fullWidth
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("map.project")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      padding: "10px 16px",
                    },
                  }}
                />
              )}
            />
            <AddMenu />
          </Box>

          <Divider />

          {/* Index Menu */}
          <DesktopIndexMenu indices={indices} />
          <IndexInfo index={currentIndex} />
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
